<template>
  <div class="protests">
    <div class="protests__activator">
      <transition name="scale-transition">
        <app-btn
          v-show="hasProtests"
          round
          color="red-grad"
          @click="isActive = !isActive"
        >
          <span v-if="isOnlyNewProtests"
            >Новые протесты:&nbsp;<b>{{ newProtests.length }}</b></span
          >
          <template v-else>
            <span
              >Протестов всего:&nbsp;<b>{{ protests.length }}</b></span
            >
            <span v-if="newProtests.length"
              >&nbsp;Новых:&nbsp;<b>{{ newProtests.length }}</b></span
            >
          </template>
        </app-btn>
      </transition>
    </div>
    <div class="protests__list">
      <transition name="slide-y-reverse-transition" appear>
        <transition-group
          class="protests__list_wrap"
          tag="div"
          name="protest-expand"
          v-if="isActive"
          @enter="onEnter"
          @leave="onLeave"
          @after-enter="onAfterEnter"
        >
          <div
            v-for="match in sortedProtests"
            :key="match.id"
            class="protest__item"
            :class="{
              protest__item_visited: visitedMatches.includes(match.id)
            }"
          >
            <div class="protest__item_wrap">
              <div class="protest__item_content">
                <div class="title ellipsis">
                  {{
                    visitedMatches.includes(match.id)
                      ? "Протест"
                      : "Новый протест"
                  }}
                  в матче #{{ match.id }}
                </div>
                <div class="text ellipsis">
                  Игра: <b>{{ match.lobby_name }}</b>
                </div>
              </div>
              <div class="protest__item_action">
                <app-btn
                  :disabled="
                    isProtestedMatchPage && routeParams.match_id === match.id
                  "
                  :color="
                    visitedMatches.includes(match.id) ? 'red-visited' : 'red'
                  "
                  @click.stop="routeToMatchRoom(match)"
                >
                  <span class="nowrap">К матчу</span>
                </app-btn>
              </div>
            </div>
          </div>
        </transition-group>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { convertToUnit } from "@/core/util/helpers";

export default {
  data() {
    return {
      isActive: true,
      visitedMatches: []
    };
  },

  computed: {
    ...mapGetters(["routeParams"]),
    ...mapGetters("auth", ["isReferee"]),
    ...mapGetters("tournaments", ["protests"]),

    hasProtests() {
      return this.isReferee && this.protests.length;
    },

    isProtestedMatchPage() {
      const id = this.routeParams.match_id;
      return (
        this.hasProtests && this.protests.filter(m => m.id === id).length > 0
      );
    },

    visited() {
      return this.protests.filter(m => this.visitedMatches.includes(m.id));
    },

    newProtests() {
      return this.protests.filter(m => !this.visitedMatches.includes(m.id));
    },

    sortedProtests() {
      return [].concat(this.visited, this.newProtests);
    },

    isOnlyNewProtests() {
      return (
        this.hasProtests && this.protests.length === this.newProtests.length
      );
    }
  },

  watch: {
    hasProtests(after, before) {
      if (before && !after) {
        this.isActive = false;
      }
    },

    isProtestedMatchPage: {
      immediate: true,
      handler: function(after) {
        if (after) {
          this.isActive = false;

          if (!this.visitedMatches.includes(this.routeParams.match_id)) {
            this.visitedMatches.push(this.routeParams.match_id);
          }
        }
      }
    }
  },

  methods: {
    routeToMatchRoom({ tournament_id, id }) {
      if (this.isProtestedMatchPage && this.routeParams.match_id === id)
        return false;

      this.isActive = !this.isActive;

      const href = this.$router.resolve({
        name: "tournament-match",
        params: { tournament_id, match_id: id }
      }).href;

      this.$nextTick(() => {
        if (!this.visitedMatches.includes(id)) this.visitedMatches.push(id);
        this.$router.push(href);
      });
    },

    onEnter(el) {
      const height = convertToUnit(el.scrollHeight);
      el.style.height = 0;
      setTimeout(() => {
        el.style.height = height;
      });
    },
    onLeave(el) {
      const height = convertToUnit(el.scrollHeight);
      el.style.height = height;
      setTimeout(() => {
        el.style.height = 0;
      });
    },
    onAfterEnter(el) {
      el.style.height = `auto`;
    }
  }
};
</script>

<style lang="scss">
.protest-expand {
  &-enter-active,
  &-leave-active {
    transition: height 0.3s ease-in-out, opacity 0.15s ease-in-out;
    overflow: hidden;
  }

  &-enter,
  &-leave-to {
    height: 0;
    opacity: 0;
  }
}

.protests {
  position: fixed;
  z-index: 10;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  pointer-events: none;

  &__activator {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 16px;

    .btn {
      font-weight: normal;
      text-transform: none;
      pointer-events: auto;
    }
  }

  &__list {
    overflow: hidden;
    position: absolute;
    z-index: 11;
    left: 0;
    top: 0;
    height: 100%;
    max-width: 100%;
    padding: 16px;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: 72px;
  }

  * {
    will-change: height;
    transform: translateZ(0);
    backface-visibility: hidden;
  }
}

.protest__item {
  overflow: hidden;
  transition: 0.3s;
  user-select: none;

  &_wrap {
    margin-top: 16px;
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 100%;
    border-radius: 6px;
    padding: 16px;

    background: #000a25;
    border: 2px solid #e72f31;
    pointer-events: auto;

    .protest__item_visited & {
      border: 2px solid #300c0d;
    }
  }

  &_content {
    margin-right: 16px;
    width: 100%;
    max-width: 400px;
    flex: 0 1 auto;
    overflow: hidden;

    .title,
    .text {
      text-align: left;
      // white-space: nowrap;
    }

    .title {
      color: #e72f31;
    }
  }

  &_action {
    display: flex;
    align-items: center;
    flex: 1 0 auto;

    .btn {
      font-size: 12px;
      padding: 5px 8px;
    }
  }
}
</style>
